export interface LiveComposerEvent {
  id: string;
  midiNotes: number[];
  accidentalSpellings?: Record<number, AccidentalSpelling>;
  capturedAtMs: number;
  source: 'midi' | 'audio' | 'manual';
  restBeforeDivisions: number;
  durationDivisions: number;
  startDivisions?: number;
  staff?: StaffNumber;
  endedAtMs?: number;
}

export interface LiveComposerPedalEvent {
  id: string;
  capturedAtMs: number;
  source: 'midi';
  pedalDown: boolean;
  positionDivisions: number;
}

export interface LiveComposerScoreBuild {
  xml: string;
  measureCount: number;
}

export interface LiveComposerMusicXmlMetadata {
  title?: string;
  composer?: string;
}

export type StaffNumber = 1 | 2;
export type AccidentalSpelling =
  | 'auto'
  | 'double-flat'
  | 'flat'
  | 'natural'
  | 'sharp'
  | 'double-sharp';
type PitchStep = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
type DurationType = '16th' | 'eighth' | 'quarter' | 'half' | 'whole';
type OrnamentType = 'trill';
type NotatedComposerEvent = Pick<
  LiveComposerEvent,
  | 'midiNotes'
  | 'accidentalSpellings'
  | 'restBeforeDivisions'
  | 'durationDivisions'
  | 'startDivisions'
  | 'staff'
> & {
  ornament?: OrnamentType;
};

export type TimeSignatureOption = {
  id: string;
  label: string;
  beats: number;
  beatType: number;
};

const DIVISIONS = 4;
const MIN_MEASURES = 4;
const STAFF_SPLIT_MIDI = 60;
const TRILL_MIN_NOTES = 4;
const TRILL_MAX_INTERVAL_SEMITONES = 2;
const TRILL_REQUIRED_DIVISIONS = 1;

export const DEFAULT_TIME_SIGNATURE: TimeSignatureOption = {
  id: '4/4',
  label: '4/4',
  beats: 4,
  beatType: 4,
};

export const COMMON_TIME_SIGNATURES: TimeSignatureOption[] = [
  { id: '2/2', label: '2/2', beats: 2, beatType: 2 },
  { id: '2/4', label: '2/4', beats: 2, beatType: 4 },
  { id: '3/4', label: '3/4', beats: 3, beatType: 4 },
  DEFAULT_TIME_SIGNATURE,
  { id: '5/4', label: '5/4', beats: 5, beatType: 4 },
  { id: '6/4', label: '6/4', beats: 6, beatType: 4 },
  { id: '3/8', label: '3/8', beats: 3, beatType: 8 },
  { id: '5/8', label: '5/8', beats: 5, beatType: 8 },
  { id: '6/8', label: '6/8', beats: 6, beatType: 8 },
  { id: '7/8', label: '7/8', beats: 7, beatType: 8 },
  { id: '9/8', label: '9/8', beats: 9, beatType: 8 },
  { id: '12/8', label: '12/8', beats: 12, beatType: 8 },
];

export const getMeasureDurationDivisions = (timeSignature: TimeSignatureOption) =>
  Math.max(1, Math.round(timeSignature.beats * DIVISIONS * (4 / timeSignature.beatType)));

const DURATION_PARTS: Array<{ duration: number; type: DurationType }> = [
  { duration: 16, type: 'whole' },
  { duration: 8, type: 'half' },
  { duration: 4, type: 'quarter' },
  { duration: 2, type: 'eighth' },
  { duration: 1, type: '16th' },
];

const normalizeMidiNotes = (midiNotes: number[]) =>
  Array.from(new Set(midiNotes.map((midi) => Math.max(0, Math.min(127, Math.round(midi)))))).sort(
    (a, b) => a - b,
  );

const escapeXmlText = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const pitchClassMap: Array<{ step: PitchStep; alter: number | null }> = [
  { step: 'C', alter: null },
  { step: 'C', alter: 1 },
  { step: 'D', alter: null },
  { step: 'D', alter: 1 },
  { step: 'E', alter: null },
  { step: 'F', alter: null },
  { step: 'F', alter: 1 },
  { step: 'G', alter: null },
  { step: 'G', alter: 1 },
  { step: 'A', alter: null },
  { step: 'A', alter: 1 },
  { step: 'B', alter: null },
];

const naturalPitchClasses: Array<{ step: PitchStep; pitchClass: number }> = [
  { step: 'C', pitchClass: 0 },
  { step: 'D', pitchClass: 2 },
  { step: 'E', pitchClass: 4 },
  { step: 'F', pitchClass: 5 },
  { step: 'G', pitchClass: 7 },
  { step: 'A', pitchClass: 9 },
  { step: 'B', pitchClass: 11 },
];

const accidentalSpellingToAlter: Record<Exclude<AccidentalSpelling, 'auto'>, number> = {
  'double-flat': -2,
  flat: -1,
  natural: 0,
  sharp: 1,
  'double-sharp': 2,
};

const accidentalSpellingToMusicXml: Record<Exclude<AccidentalSpelling, 'auto'>, string> = {
  'double-flat': 'flat-flat',
  flat: 'flat',
  natural: 'natural',
  sharp: 'sharp',
  'double-sharp': 'double-sharp',
};

const getPreferredPitch = (
  normalizedMidi: number,
  spelling: Exclude<AccidentalSpelling, 'auto'>,
) => {
  const targetAlter = accidentalSpellingToAlter[spelling];
  const pitchClass = ((normalizedMidi % 12) + 12) % 12;
  const candidate = naturalPitchClasses.find(
    (naturalPitch) => (naturalPitch.pitchClass + targetAlter + 120) % 12 === pitchClass,
  );

  if (!candidate) return null;

  const octave = (normalizedMidi - targetAlter - candidate.pitchClass) / 12 - 1;
  if (!Number.isInteger(octave)) return null;

  return {
    step: candidate.step,
    alter: targetAlter,
    octave,
    accidental: accidentalSpellingToMusicXml[spelling],
  };
};

const midiToPitch = (midi: number, spelling: AccidentalSpelling = 'auto') => {
  const normalizedMidi = Math.max(0, Math.min(127, Math.round(midi)));
  if (spelling !== 'auto') {
    const preferredPitch = getPreferredPitch(normalizedMidi, spelling);
    if (preferredPitch) return preferredPitch;
  }

  const pitchClass = pitchClassMap[((normalizedMidi % 12) + 12) % 12];
  const octave = Math.floor(normalizedMidi / 12) - 1;
  return {
    step: pitchClass.step,
    alter: pitchClass.alter,
    octave,
    accidental: null,
  };
};

const buildPitchXml = (midi: number, spelling: AccidentalSpelling = 'auto') => {
  const pitch = midiToPitch(midi, spelling);
  return [
    '<pitch>',
    `<step>${pitch.step}</step>`,
    pitch.alter == null || pitch.alter === 0 ? '' : `<alter>${pitch.alter}</alter>`,
    `<octave>${pitch.octave}</octave>`,
    '</pitch>',
  ].join('');
};

const buildAccidentalXml = (midi: number, spelling: AccidentalSpelling = 'auto') => {
  if (spelling === 'auto') return '';
  const pitch = midiToPitch(midi, spelling);
  return pitch.accidental ? `<accidental>${pitch.accidental}</accidental>` : '';
};

const buildRestXml = (staff: StaffNumber, duration: number, type: DurationType, measure = false) =>
  [
    '<note>',
    measure ? '<rest measure="yes"/>' : '<rest/>',
    `<duration>${duration}</duration>`,
    `<voice>${staff}</voice>`,
    `<type>${type}</type>`,
    `<staff>${staff}</staff>`,
    '</note>',
  ].join('');

const buildPedalDirectionXml = (pedalDown: boolean, offset: number) =>
  [
    '<direction placement="below">',
    '<direction-type>',
    `<pedal type="${pedalDown ? 'start' : 'stop'}"/>`,
    '</direction-type>',
    offset > 0 ? `<offset sound="yes">${offset}</offset>` : '',
    '<staff>1</staff>',
    '</direction>',
  ].join('');

const buildOrnamentXml = (ornament?: OrnamentType) => {
  if (ornament === 'trill') {
    return '<ornaments><trill-mark/></ornaments>';
  }
  return '';
};

const buildNotationsXml = (tieStop: boolean, tieStart: boolean, ornament?: OrnamentType) => {
  const contents = [
    tieStop ? '<tied type="stop"/>' : '',
    tieStart ? '<tied type="start"/>' : '',
    buildOrnamentXml(ornament),
  ]
    .filter(Boolean)
    .join('');

  return contents ? `<notations>${contents}</notations>` : '';
};

const buildChordXml = (
  midiNotes: number[],
  accidentalSpellings: Record<number, AccidentalSpelling> | undefined,
  staff: StaffNumber,
  duration: number,
  type: DurationType,
  tieStop: boolean,
  tieStart: boolean,
  ornament?: OrnamentType,
) => {
  const sortedNotes = normalizeMidiNotes(midiNotes);

  if (sortedNotes.length === 0) {
    return buildRestXml(staff, duration, type);
  }

  return sortedNotes
    .map((midi, index) =>
      [
        '<note>',
        index === 0 ? '' : '<chord/>',
        buildPitchXml(midi, accidentalSpellings?.[midi]),
        `<duration>${duration}</duration>`,
        `<voice>${staff}</voice>`,
        `<type>${type}</type>`,
        buildAccidentalXml(midi, accidentalSpellings?.[midi]),
        tieStop ? '<tie type="stop"/>' : '',
        tieStart ? '<tie type="start"/>' : '',
        `<staff>${staff}</staff>`,
        buildNotationsXml(tieStop, tieStart, index === 0 ? ornament : undefined),
        '</note>',
      ].join(''),
    )
    .join('');
};

const splitStaffNotes = (event: Pick<LiveComposerEvent, 'midiNotes' | 'staff'>) => {
  if (event.staff === 1) {
    return { treble: event.midiNotes, bass: [] };
  }
  if (event.staff === 2) {
    return { treble: [], bass: event.midiNotes };
  }

  const treble = event.midiNotes.filter((midi) => midi >= STAFF_SPLIT_MIDI);
  const bass = event.midiNotes.filter((midi) => midi < STAFF_SPLIT_MIDI);
  return { treble, bass };
};

const pickAccidentalSpellings = (
  accidentalSpellings: Record<number, AccidentalSpelling> | undefined,
  midiNotes: number[],
) => {
  if (!accidentalSpellings) return undefined;

  const nextSpellings: Record<number, AccidentalSpelling> = {};
  normalizeMidiNotes(midiNotes).forEach((midi) => {
    const spelling = accidentalSpellings[midi];
    if (spelling && spelling !== 'auto') {
      nextSpellings[midi] = spelling;
    }
  });

  return Object.keys(nextSpellings).length > 0 ? nextSpellings : undefined;
};

const getSingleNoteStaff = (event: LiveComposerEvent): StaffNumber | null => {
  if (event.staff) return event.staff;
  if (event.midiNotes.length !== 1) return null;
  return event.midiNotes[0] >= STAFF_SPLIT_MIDI ? 1 : 2;
};

const buildNotationEvents = (events: LiveComposerEvent[]): NotatedComposerEvent[] => {
  const notationEvents: NotatedComposerEvent[] = [];

  for (let index = 0; index < events.length; ) {
    const firstEvent = events[index];
    const firstStaff = getSingleNoteStaff(firstEvent);
    const firstNote = firstEvent.midiNotes[0];

    if (
      firstStaff == null ||
      firstEvent.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
      firstEvent.midiNotes.length !== 1
    ) {
      notationEvents.push({
        midiNotes: firstEvent.midiNotes,
        accidentalSpellings: firstEvent.accidentalSpellings,
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: firstEvent.durationDivisions,
        startDivisions: firstEvent.startDivisions,
        staff: firstEvent.staff,
      });
      index += 1;
      continue;
    }

    const secondEvent = events[index + 1];
    const secondStaff = secondEvent ? getSingleNoteStaff(secondEvent) : null;
    const secondNote = secondEvent?.midiNotes[0];

    if (
      !secondEvent ||
      secondStaff == null ||
      secondStaff !== firstStaff ||
      secondEvent.restBeforeDivisions !== 0 ||
      secondEvent.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
      secondNote == null ||
      secondNote === firstNote ||
      Math.abs(secondNote - firstNote) > TRILL_MAX_INTERVAL_SEMITONES
    ) {
      notationEvents.push({
        midiNotes: firstEvent.midiNotes,
        accidentalSpellings: firstEvent.accidentalSpellings,
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: firstEvent.durationDivisions,
        startDivisions: firstEvent.startDivisions,
        staff: firstEvent.staff,
      });
      index += 1;
      continue;
    }

    let runLength = 2;
    let lookaheadIndex = index + 2;

    while (lookaheadIndex < events.length) {
      const candidate = events[lookaheadIndex];
      const candidateStaff = getSingleNoteStaff(candidate);
      const expectedNote = runLength % 2 === 0 ? firstNote : secondNote;

      if (
        candidateStaff == null ||
        candidateStaff !== firstStaff ||
        candidate.restBeforeDivisions !== 0 ||
        candidate.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
        candidate.midiNotes[0] !== expectedNote
      ) {
        break;
      }

      runLength += 1;
      lookaheadIndex += 1;
    }

    if (runLength >= TRILL_MIN_NOTES) {
      notationEvents.push({
        midiNotes: [firstNote],
        accidentalSpellings: pickAccidentalSpellings(firstEvent.accidentalSpellings, [firstNote]),
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: runLength * TRILL_REQUIRED_DIVISIONS,
        startDivisions: firstEvent.startDivisions,
        staff: firstEvent.staff,
        ornament: 'trill',
      });
      index += runLength;
      continue;
    }

    notationEvents.push({
      midiNotes: firstEvent.midiNotes,
      accidentalSpellings: firstEvent.accidentalSpellings,
      restBeforeDivisions: firstEvent.restBeforeDivisions,
      durationDivisions: firstEvent.durationDivisions,
      startDivisions: firstEvent.startDivisions,
      staff: firstEvent.staff,
    });
    index += 1;
  }

  return notationEvents;
};

const decomposeDuration = (duration: number) => {
  const parts: Array<{ duration: number; type: DurationType }> = [];
  let remaining = Math.max(0, Math.round(duration));

  for (const part of DURATION_PARTS) {
    while (remaining >= part.duration) {
      parts.push(part);
      remaining -= part.duration;
    }
  }

  return parts;
};

const appendStaffMaterial = ({
  notes,
  accidentalSpellings,
  staff,
  startDivision,
  durationDivisions,
  ornament,
  measureDuration,
  measureXml,
}: {
  notes: number[];
  accidentalSpellings?: Record<number, AccidentalSpelling>;
  staff: StaffNumber;
  startDivision: number;
  durationDivisions: number;
  ornament?: OrnamentType;
  measureDuration: number;
  measureXml: Array<{ treble: string[]; bass: string[] }>;
}) => {
  let cursor = startDivision;
  let remaining = Math.max(1, Math.round(durationDivisions));
  const noteChunks: Array<{
    measureIndex: number;
    duration: number;
    type: DurationType;
  }> = [];

  while (remaining > 0) {
    const measureIndex = Math.floor(cursor / measureDuration);
    const measureOffset = cursor % measureDuration;
    const availableInMeasure = measureDuration - measureOffset;
    const chunkDuration = Math.min(remaining, availableInMeasure);
    const parts = decomposeDuration(chunkDuration);

    parts.forEach((part) => {
      noteChunks.push({
        measureIndex,
        duration: part.duration,
        type: part.type,
      });
    });

    cursor += chunkDuration;
    remaining -= chunkDuration;
  }

  noteChunks.forEach((chunk, index) => {
    const xml = buildChordXml(
      notes,
      accidentalSpellings,
      staff,
      chunk.duration,
      chunk.type,
      index > 0,
      index < noteChunks.length - 1,
      index === 0 ? ornament : undefined,
    );

    if (staff === 1) {
      measureXml[chunk.measureIndex].treble.push(xml);
    } else {
      measureXml[chunk.measureIndex].bass.push(xml);
    }
  });
};

const buildMeasureXml = (
  measureIndex: number,
  measure: { directions: string[]; treble: string[]; bass: string[] },
  timeSignature: TimeSignatureOption,
  measureDuration: number,
) => {
  const attributes =
    measureIndex === 0
      ? [
          '<attributes>',
          `<divisions>${DIVISIONS}</divisions>`,
          '<key><fifths>0</fifths></key>',
          `<time><beats>${timeSignature.beats}</beats><beat-type>${timeSignature.beatType}</beat-type></time>`,
          '<staves>2</staves>',
          '<clef number="1"><sign>G</sign><line>2</line></clef>',
          '<clef number="2"><sign>F</sign><line>4</line></clef>',
          '</attributes>',
        ].join('')
      : '';

  const trebleXml =
    measure.treble.length > 0
      ? measure.treble.join('')
      : buildRestXml(1, measureDuration, 'whole', true);
  const bassXml =
    measure.bass.length > 0
      ? measure.bass.join('')
      : buildRestXml(2, measureDuration, 'whole', true);

  return [
    `<measure number="${measureIndex + 1}">`,
    attributes,
    measure.directions.join(''),
    trebleXml,
    `<backup><duration>${measureDuration}</duration></backup>`,
    bassXml,
    '</measure>',
  ].join('');
};

type TimelineComposerEvent = NotatedComposerEvent & {
  startDivisions: number;
  durationDivisions: number;
};

type StaffTimelineEntry = {
  notes: number[];
  accidentalSpellings?: Record<number, AccidentalSpelling>;
  startDivisions: number;
  durationDivisions: number;
  ornament?: OrnamentType;
};

const buildTimelineEvents = (events: NotatedComposerEvent[]): TimelineComposerEvent[] => {
  let cursorDivision = 0;

  return events
    .map((event) => {
      const restBeforeDivisions = Math.max(0, Math.round(event.restBeforeDivisions));
      const durationDivisions = Math.max(1, Math.round(event.durationDivisions));
      const startDivisions =
        event.startDivisions == null
          ? cursorDivision + restBeforeDivisions
          : Math.max(0, Math.round(event.startDivisions));
      cursorDivision =
        event.startDivisions == null
          ? cursorDivision + restBeforeDivisions + durationDivisions
          : Math.max(cursorDivision, startDivisions + durationDivisions);

      return {
        ...event,
        startDivisions,
        durationDivisions,
      };
    })
    .sort(
      (a, b) =>
        a.startDivisions - b.startDivisions ||
        a.durationDivisions - b.durationDivisions ||
        a.midiNotes[0] - b.midiNotes[0],
    );
};

const buildStaffTimelineEntries = (timelineEvents: TimelineComposerEvent[], staff: StaffNumber) => {
  const entries = timelineEvents
    .reduce<StaffTimelineEntry[]>((acc, event) => {
      const { treble, bass } = splitStaffNotes(event);
      const notes = staff === 1 ? treble : bass;
      if (notes.length > 0) {
        acc.push({
          notes,
          accidentalSpellings: pickAccidentalSpellings(event.accidentalSpellings, notes),
          startDivisions: event.startDivisions,
          durationDivisions: event.durationDivisions,
          ornament: event.ornament,
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.startDivisions - b.startDivisions || a.notes[0] - b.notes[0]);

  const mergedEntries: StaffTimelineEntry[] = [];
  entries.forEach((entry) => {
    const previous = mergedEntries[mergedEntries.length - 1];
    if (!previous) {
      mergedEntries.push({ ...entry, notes: [...entry.notes] });
      return;
    }

    const previousEnd = previous.startDivisions + previous.durationDivisions;
    const entryEnd = entry.startDivisions + entry.durationDivisions;
    if (entry.startDivisions < previousEnd) {
      previous.notes = Array.from(new Set([...previous.notes, ...entry.notes])).sort(
        (a, b) => a - b,
      );
      previous.accidentalSpellings = {
        ...(previous.accidentalSpellings ?? {}),
        ...(entry.accidentalSpellings ?? {}),
      };
      previous.durationDivisions = Math.max(previousEnd, entryEnd) - previous.startDivisions;
      previous.ornament = previous.ornament ?? entry.ornament;
      return;
    }

    mergedEntries.push({ ...entry, notes: [...entry.notes] });
  });

  return mergedEntries;
};

export const buildLiveComposerMusicXml = (
  events: LiveComposerEvent[],
  pedalEvents: LiveComposerPedalEvent[] = [],
  timeSignature: TimeSignatureOption = DEFAULT_TIME_SIGNATURE,
  metadata: LiveComposerMusicXmlMetadata = {},
): LiveComposerScoreBuild => {
  const notationEvents = buildNotationEvents(events);
  const timelineEvents = buildTimelineEvents(notationEvents);
  const measureDuration = getMeasureDurationDivisions(timeSignature);
  const totalDurationDivisions = timelineEvents.reduce(
    (max, event) => Math.max(max, event.startDivisions + event.durationDivisions),
    0,
  );
  const furthestPedalDivision = pedalEvents.reduce(
    (max, event) => Math.max(max, Math.max(0, Math.round(event.positionDivisions))),
    0,
  );
  const measureCount = Math.max(
    MIN_MEASURES,
    Math.ceil(
      (Math.max(totalDurationDivisions, furthestPedalDivision, 1) + measureDuration) /
        measureDuration,
    ),
  );

  const measureXml = Array.from({ length: measureCount }, () => ({
    directions: [] as string[],
    treble: [] as string[],
    bass: [] as string[],
  }));

  ([1, 2] as StaffNumber[]).forEach((staff) => {
    const staffEntries = buildStaffTimelineEntries(timelineEvents, staff);
    let staffCursorDivision = 0;

    staffEntries.forEach((event) => {
      const restBeforeDivisions = Math.max(0, event.startDivisions - staffCursorDivision);
      if (restBeforeDivisions > 0) {
        appendStaffMaterial({
          notes: [],
          accidentalSpellings: undefined,
          staff,
          startDivision: staffCursorDivision,
          durationDivisions: restBeforeDivisions,
          measureDuration,
          measureXml,
        });
      }

      appendStaffMaterial({
        notes: event.notes,
        accidentalSpellings: event.accidentalSpellings,
        staff,
        startDivision: Math.max(staffCursorDivision, event.startDivisions),
        durationDivisions: event.durationDivisions,
        ornament: event.ornament,
        measureDuration,
        measureXml,
      });

      staffCursorDivision = Math.max(
        staffCursorDivision,
        event.startDivisions + event.durationDivisions,
      );
    });

    if (staffEntries.length > 0) {
      const measureEndDivision = Math.ceil(staffCursorDivision / measureDuration) * measureDuration;
      const trailingRestDivisions = Math.max(0, measureEndDivision - staffCursorDivision);
      if (trailingRestDivisions > 0) {
        appendStaffMaterial({
          notes: [],
          accidentalSpellings: undefined,
          staff,
          startDivision: staffCursorDivision,
          durationDivisions: trailingRestDivisions,
          measureDuration,
          measureXml,
        });
      }
    }
  });

  pedalEvents
    .slice()
    .sort((a, b) => a.positionDivisions - b.positionDivisions || a.capturedAtMs - b.capturedAtMs)
    .forEach((event) => {
      const absoluteDivisions = Math.max(0, Math.round(event.positionDivisions));
      const measureIndex = Math.floor(absoluteDivisions / measureDuration);
      if (!measureXml[measureIndex]) return;
      const offset = absoluteDivisions % measureDuration;
      measureXml[measureIndex].directions.push(buildPedalDirectionXml(event.pedalDown, offset));
    });

  const measures = measureXml
    .map((measure, measureIndex) =>
      buildMeasureXml(measureIndex, measure, timeSignature, measureDuration),
    )
    .join('');
  const title = metadata.title?.trim();
  const composer = metadata.composer?.trim();
  const metadataXml = [
    title ? `  <work><work-title>${escapeXmlText(title)}</work-title></work>` : '',
    composer
      ? `  <identification><creator type="composer">${escapeXmlText(
          composer,
        )}</creator></identification>`
      : '',
  ]
    .filter(Boolean)
    .join('\n');
  const creditXml =
    title || composer
      ? `  <credit page="1">
    <credit-words default-x="620" default-y="1500" justify="center" valign="top" font-size="18">${escapeXmlText(
      title || 'Untitled Repertoire Draft',
    )}</credit-words>
  </credit>
  <credit page="1">
    <credit-words default-x="1180" default-y="1440" justify="right" valign="top" font-size="12">${escapeXmlText(
      composer || 'you',
    )}</credit-words>
  </credit>`
      : '';

  const xml = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<!DOCTYPE score-partwise PUBLIC
  "-//Recordare//DTD MusicXML 3.1 Partwise//EN"
  "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
${metadataXml ? `${metadataXml}\n` : ''}${creditXml ? `${creditXml}\n` : ''}  <part-list>
    <score-part id="P1">
      <part-name>Live Draft</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    ${measures}
  </part>
</score-partwise>`;

  return {
    xml,
    measureCount,
  };
};
